import React, { useEffect, useState, useRef, useMemo } from "react";
import { HubConnectionBuilder, LogLevel, HttpTransportType } from "@microsoft/signalr";
import { LineChart, Line, PieChart, Pie, Cell } from "recharts";
import "./ServerMonitorDashboard.css";

const HUB_URL = "https://serverhub.apps.bocek.com.tr/statushub";

const getUsageColor = (percent) => {
    if (percent <= 50) return '#00C49F'; 
    if (percent <= 75) return '#FFC107'; 
    return '#f44336'; 
};

const getBaseDomain = (domain) => {
    if (!domain) return '';
    const parts = domain.split('.');
    if (parts.length <= 2) return domain;
    const sld = parts[parts.length - 2];
    if (['com', 'net', 'org', 'edu', 'gov', 'web', 'gen', 'k12'].includes(sld)) {
        return parts.slice(-3).join('.');
    }
    return parts.slice(-2).join('.');
};

const ResourceBar = ({ used, total }) => {
    const percentage = total > 0 ? Math.min((used / total) * 100, 100) : 0;
    const barColor = getColorFromUsage(used, total);
    return (
        <div style={{
            position: 'relative', width: '100px', height: '6px',
            backgroundColor: '#333', borderRadius: '4px', overflow: 'hidden', marginTop: '4px'
        }}>
            <div style={{ width: `${percentage}%`, height: '100%', backgroundColor: barColor }} />
        </div>
    );
};

const getColorFromUsage = (used, total) => {
    const pct = total > 0 ? (used / total) * 100 : 0;
    if (pct > 80) return "#f44336";
    if (pct > 40) return "#ff9800";
    return "#4caf50";
};

const Donut = ({ used, total }) => (
    <PieChart width={100} height={100}>
        <Pie
            data={[{ value: used }, { value: Math.max(0, total - used) }]}
            innerRadius={30} outerRadius={40} startAngle={90} endAngle={-270} dataKey="value"
        >
            <Cell fill={getUsageColor(total > 0 ? (used / total) * 100 : 0)} />
            <Cell fill="#444" />
        </Pie>
    </PieChart>
);

const StatBox = ({ label, val }) => (
    <div className="stat-box center-box">
        <span className="stat-big">{val}</span>
        <span className="stat-label">{label}</span>
    </div>
);

const SlideNumber = ({ value, delay = 0 }) => {
    const [display, setDisplay] = useState(String(value));
    const [key, setKey] = useState(0);

    useEffect(() => {
        const valStr = String(value);
        if (valStr !== display) {
            if (delay > 0) {
                const timer = setTimeout(() => {
                    setDisplay(valStr);
                    setKey(k => k + 1);
                }, delay);
                return () => clearTimeout(timer);
            } else {
                setDisplay(valStr);
                setKey(k => k + 1);
            }
        }
    }, [value, display, delay]);

    return (
        <span key={key} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
            {display.split('').map((char, index) => (
                <span 
                    key={index} 
                    style={{ 
                        display: 'inline-block', 
                        animation: `slideUpIn 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) both`,
                        animationDelay: `${index * 100}ms`
                    }}
                >
                    {char === ' ' ? '\u00A0' : char}
                </span>
            ))}
        </span>
    );
};

const getStatusProps = (ms, type) => {
    if (ms === -1 || ms == null) return { text: 'Kapalı', color: '#ff4444', bg: 'rgba(255, 68, 68, 0.15)' };
    
    if (type === 'DB') {
      if (ms <= 50) return { text: 'İyi', color: '#4caf50', bg: 'rgba(76, 175, 80, 0.15)' };
      if (ms <= 200) return { text: 'Uyarı', color: '#ffb300', bg: 'rgba(255, 179, 0, 0.15)' };
      return { text: 'Kritik', color: '#ff4444', bg: 'rgba(255, 68, 68, 0.15)' };
    } 
    
    if (type === 'API') {
      if (ms <= 300) return { text: 'İyi', color: '#4caf50', bg: 'rgba(76, 175, 80, 0.15)' };
      if (ms <= 1000) return { text: 'Uyarı', color: '#ffb300', bg: 'rgba(255, 179, 0, 0.15)' };
      return { text: 'Kritik', color: '#ff4444', bg: 'rgba(255, 68, 68, 0.15)' };
    }
  };

const getOsIcon = (osVersion) => {
    let iconName = 'unknown';
    if (!osVersion) {
        iconName = 'windows';
    } else {
        const os = osVersion.toLowerCase();
        if (os.includes('win')) iconName = 'windows';
        else if (os.includes('ubuntu') || os.includes('linux') || os.includes('debian') || os.includes('centos') || os.includes('redhat') || os.includes('almalinux')) iconName = 'linux';
        else iconName = 'unknown';
    }
    return <img src={`/${iconName}.svg`} alt={iconName} style={{ width: '18px', height: '18px', objectFit: 'contain', verticalAlign: 'middle', marginRight: '6px' }} />;
};

const getSslStyles = (type) => {
    switch (type) {
        case 'expired': return { bg: '#F44336', color: '#fff' };
        case 'warning': return { bg: '#FF9800', color: '#fff' };
        case 'active':  return { bg: '#4CAF50', color: '#fff' };
        default:        return { bg: '#555', color: '#fff' };
    }
};

const parseSslItem = (item) => {
    const parts = item.split(' (');
    const domain = parts[0];
    const statusRaw = parts[1] ? parts[1].replace(')', '') : 'Bilinmiyor';
    
    let isExpired = statusRaw.includes('DOLMUŞ') || statusRaw.includes('Yok') || statusRaw.includes('Erişilemedi');
    let daysLeft = null;
    let label = statusRaw;
    let type = 'active';

    if (isExpired) {
        type = 'expired';
        label = statusRaw.replace('SÜRESİ DOLMUŞ!', 'Süresi Dolmuş').replace('Yok', 'SSL Yok');
    } else {
        const daysMatch = statusRaw.match(/\d+/);
        if (daysMatch) {
            daysLeft = parseInt(daysMatch[0], 10);
            if (daysLeft <= 0) {
                type = 'expired';
                isExpired = true;
                label = 'Süresi Dolmuş';
            } else if (daysLeft <= 15) {
                type = 'warning';
                label = `${daysLeft} Gün`;
            } else {
                label = `${daysLeft} Gün`;
            }
        } else {
            label = statusRaw; 
        }
    }
    return { domain, label, type, isExpired, daysLeft };
};

const SystemStats = ({ totals }) => {
    const { serverCount, activeDomainTotal, altDomainTotal, ramTotal, ramUsed, diskTotal, diskUsed } = totals;
    
    return (
        <div className="stats-wrapper">
            <div className="stats-top">
                <StatBox label="Aktif Sunucu" val={<SlideNumber value={serverCount} delay={0} />} />
                <StatBox label="Aktif Domain" val={<SlideNumber value={activeDomainTotal} delay={200} />} />
                <StatBox label="Aktif SubDomain" val={<SlideNumber value={altDomainTotal} delay={400} />} />
            </div>
            
            <div className="stats-bottom">
                {[
                    { caption: "RAM", used: ramUsed, total: ramTotal },
                    { caption: "Disk", used: diskUsed, total: diskTotal },
                ].map(({ caption, used, total }) => (
                    <div key={caption} className="stat-box">
                        <strong className="stat-caption">{caption}</strong>
                        <Donut used={used} total={total} />
                        <span className="stat-caption">
                            {used.toFixed(1)} GB / {total.toFixed(1)} GB
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ======================= VIEW COMPONENTS =======================

const ServerRow = ({ s, i, isStale, isJustConnected, forceExpand }) => {
    const [expanded, setExpanded] = useState(false);
    const [sslFilter, setSslFilter] = useState('all');
    const [showPublicIp, setShowPublicIp] = useState(false);

    useEffect(() => {
        setExpanded(forceExpand);
    }, [forceExpand]);

    const filteredSsl = useMemo(() => {
        if (!s.sslParsed) return [];
        return s.sslParsed.filter(p => {
            if (sslFilter === 'expired') return p.type === 'expired' || p.isExpired;
            if (sslFilter === 'active') return !p.isExpired;
            return true;
        }).sort((a,b) => (a.type === 'expired' ? -1 : 1));
    }, [s.sslParsed, sslFilter]);

    return (
        <React.Fragment>
            <tr className={`server-row ${isStale(s) ? "row-stale" : isJustConnected(s) ? "row-fresh" : ""} ${expanded ? 'expanded' : ''}`}>
                <td data-label="İşlem" style={{ display: 'flex', alignItems: 'center' }}>
                    <button 
                        onClick={() => setExpanded(!expanded)}
                        style={{ background: '#333', border: '1px solid #555', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', width: '24px', height: '24px', borderRadius: '4px', marginRight: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Domain Detaylarını Göster"
                    >
                        {expanded ? '−' : '+'}
                    </button>
                    <span style={{ color: '#777' }}>#{i + 1}</span>
                </td>
                <td data-label="IP Adresi">
                    <div>{s.machineIp} <span style={{fontSize:'10px', color:'#aaa'}}>(Local)</span></div>
                    {s.publicIp && (
                        <div 
                            onClick={(e) => { e.stopPropagation(); setShowPublicIp(!showPublicIp); }} 
                            style={{ cursor: 'pointer', marginTop: '4px', fontSize: '11px', color: showPublicIp ? '#00C49F' : '#888', userSelect: 'none' }}
                            title="Dış Bacak IP'sini Göster/Gizle"
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle', marginRight:'4px'}}>
                                {showPublicIp ? (
                                    <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></>
                                ) : (
                                    <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></>
                                )}
                            </svg>
                            {showPublicIp ? s.publicIp : '***.***.*.* (Public)'}
                        </div>
                    )}
                </td>
                <td data-label="Sunucu">
                    <strong title={s.osVersion || 'Windows (Varsayılan)'} style={{ cursor: 'help' }}>
                        {getOsIcon(s.osVersion)} {s.machineName}
                    </strong>
                </td>
                <td data-label="CPU Kull.">
                    <div className="row-flex chart-box">
                        <span className="stat-text">{s.cpu.toFixed(1)}%</span>
                        <LineChart width={80} height={20} data={s.cpuHistory}>
                            <Line type="monotone" dataKey="value" stroke={getColorFromUsage(s.cpu, 100)} strokeWidth={2} dot={false}/>
                        </LineChart>
                    </div>
                </td>
                <td data-label="RAM Kull.">
                    <div className="row-flex-col">
                        <span className="stat-text">{s.ramUsed.toFixed(1)}G / {s.ramTotal.toFixed(1)}G</span>
                        <ResourceBar used={s.ramUsed} total={s.ramTotal}/>
                    </div>
                </td>
                <td data-label="Disk Kull.">
                    <div className="row-flex-col">
                        <span className="stat-text">{s.diskUsed.toFixed(0)}G / {s.diskTotal.toFixed(0)}G</span>
                        <ResourceBar used={s.diskUsed} total={s.diskTotal}/>
                    </div>
                </td>
                <td data-label="API / DB">
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        {(() => {
                            const apiSt = getStatusProps(s.apiResponseTime, 'API');
                            const dbSt = getStatusProps(s.dbPerformance, 'DB');
                            return (
                                <>
                                  <span className="api-span" style={{ backgroundColor: apiSt.bg, color: apiSt.color, padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>API: {s.apiResponseTime == null || s.apiResponseTime === -1 ? '-' : `${s.apiResponseTime}ms`}</span>
                                  <span className="db-span" style={{ backgroundColor: dbSt.bg, color: dbSt.color, padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>DB: {s.dbPerformance == null || s.dbPerformance === -1 ? '-' : `${s.dbPerformance}ms`}</span>
                                </>
                            );
                        })()}
                    </div>
                </td>
                <td data-label="Persits & SSL">
                    <div className="status-group">
                        {['Upload', 'Jpeg', 'Mail'].map((label) => {
                            const field = `persits${label}`;
                            return (
                                <span key={label} className={`status ${s[field] ? 'ok' : 'fail'}`}>
                                    {label}
                                </span>
                            );
                        })}
                        <span className={`status ${s.sslSummary.status === 'ok' ? 'ok' : 'fail'}`} title="SSL Genel Durum">
                            {s.sslSummary.status === 'fail' ? 'SSL:' : ''} {s.sslSummary.label}
                        </span>
                    </div>
                </td>
            </tr>
            {expanded && (
                <tr className="details-row">
                    <td colSpan="8" style={{ padding: 0, backgroundColor: '#1a1a1a' }}>
                        <div style={{ padding: '15px 20px', borderBottom: '1px solid #333' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <h5 style={{ margin: 0, color: '#03A9F4', fontSize: '14px', marginBottom: '6px' }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinelinejoin="round" style={{marginRight: '6px', verticalAlign: '-2px'}}>
                                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                                        </svg>
                                        {s.machineName} Sunucusu SSL Durumları ({s.sslParsed.length} Kayıt)
                                    </h5>
                                    <span style={{ fontSize: '12px', color: '#aaa', display: 'flex', alignItems: 'center' }}>
                                        {getOsIcon(s.osVersion)} <span style={{ marginLeft: '4px' }}>{s.osVersion || 'Windows (Varsayılan)'}</span>
                                    </span>
                                </div>
                                <select 
                                    value={sslFilter} 
                                    onChange={e => setSslFilter(e.target.value)}
                                    style={{ backgroundColor: '#333', color: '#fff', border: '1px solid #555', borderRadius: '4px', fontSize: '12px', padding: '4px 8px', outline: 'none' }}
                                >
                                    <option value="all">Tümü ({s.sslParsed.length})</option>
                                    <option value="expired">Erişilemeyen / Süresi Geçmiş ({s.sslParsed.filter(x => x.isExpired).length})</option>
                                    <option value="active">Aktif ({s.sslParsed.filter(x => !x.isExpired).length})</option>
                                </select>
                            </div>
                            {filteredSsl.length > 0 ? (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '8px', maxHeight: '300px', overflowY: 'auto', paddingRight: '5px' }}>
                                    {filteredSsl.map((item, idx) => {
                                        const styles = getSslStyles(item.type);
                                        return (
                                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#222', padding: '6px 10px', borderRadius: '4px', border: `1px solid ${item.isExpired ? '#F44336' : '#333'}` }}>
                                                <span style={{ color: '#ccc', wordBreak: 'break-all', fontSize: '12px' }}>{item.domain}</span>
                                                <span style={{ backgroundColor: styles.bg, color: styles.color, padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', whiteSpace: 'nowrap', marginLeft: '10px' }}>
                                                    {item.label}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div style={{ color: '#888', fontSize: '12px', fontStyle: 'italic' }}>Listelenecek kayıt bulunamadı...</div>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </React.Fragment>
    );
};

const MetricBadge = ({ server, sortField }) => {
    if (!server) return null;
    if (sortField === 'cpu') return <span className="stat-text" style={{color:'#00C49F', fontWeight:'bold'}}>{server.cpu?.toFixed(1) || 0}%</span>;
    if (sortField === 'ram') return <span className="stat-text" style={{color:'#ccc'}}>{server.ramUsed?.toFixed(1)}G / {server.ramTotal?.toFixed(1)}G</span>;
    if (sortField === 'disk') return <span className="stat-text" style={{color:'#ccc'}}>{server.diskUsed?.toFixed(0)}G / {server.diskTotal?.toFixed(0)}G</span>;
    if (sortField === 'api') {
        const st = getStatusProps(server.apiResponseTime, 'API');
        return <span style={{ backgroundColor: st.bg, color: st.color, padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>{server.apiResponseTime == null || server.apiResponseTime === -1 ? '-' : `${server.apiResponseTime}ms`}</span>;
    }
    if (sortField === 'db') {
        const st = getStatusProps(server.dbPerformance, 'DB');
        return <span style={{ backgroundColor: st.bg, color: st.color, padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>{server.dbPerformance == null || server.dbPerformance === -1 ? '-' : `${server.dbPerformance}ms`}</span>;
    }
    return null;
};

const DomainRow = ({ d, i, forceExpand, isSubRow = false, sortField }) => {
    const [expanded, setExpanded] = useState(false);
    const [showPublicIp, setShowPublicIp] = useState(false);
    const s = d.server;
    const styles = getSslStyles(d.type);

    useEffect(() => {
        setExpanded(forceExpand);
    }, [forceExpand]);

    return (
        <React.Fragment>
            <tr className={isSubRow ? 'domain-subrow' : `domain-row ${expanded ? 'expanded' : ''}`} style={{ backgroundColor: isSubRow ? 'rgba(255, 255, 255, 0.02)' : 'transparent' }}>
                <td data-label="İşlem" style={{ display: 'flex', alignItems: 'center', paddingLeft: isSubRow ? '30px' : '10px' }}>
                    <button 
                        onClick={() => setExpanded(!expanded)}
                        style={{ background: '#333', border: '1px solid #555', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', width: '24px', height: '24px', borderRadius: '4px', marginRight: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Sunucu Detaylarını Göster"
                    >
                        {expanded ? '−' : '+'}
                    </button>
                    <span style={{ color: '#777' }}>#{i + 1}</span>
                </td>
                <td data-label="Domain" style={{ textAlign: 'left', paddingLeft: isSubRow ? '15px' : '20px' }}>
                    {isSubRow && <span style={{ color: '#555', marginRight: '6px' }}>↳</span>}
                    <strong style={{ color: isSubRow ? '#00bfff' : '#03A9F4', fontSize: isSubRow ? '13px' : '14px' }}>{d.domain}</strong>
                </td>
                <td data-label="Sunucu">
                    <span title={s.osVersion || 'Windows (Varsayılan)'} style={{ cursor: 'help' }}>
                        {getOsIcon(s.osVersion)} {s.machineName}
                    </span>
                </td>
                <td data-label="IP Adresi">
                    <div>{s.machineIp} <span style={{fontSize:'10px', color:'#aaa'}}>(Local)</span></div>
                    {s.publicIp && (
                        <div 
                            onClick={(e) => { e.stopPropagation(); setShowPublicIp(!showPublicIp); }} 
                            style={{ cursor: 'pointer', marginTop: '4px', fontSize: '11px', color: showPublicIp ? '#00C49F' : '#888', userSelect: 'none' }}
                            title="Dış Bacak IP'sini Göster/Gizle"
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle', marginRight:'4px'}}>
                                {showPublicIp ? (
                                    <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></>
                                ) : (
                                    <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></>
                                )}
                            </svg>
                            {showPublicIp ? s.publicIp : '***.***.*.* (Public)'}
                        </div>
                    )}
                </td>
                {sortField && (
                    <td data-label="Metrik">
                        <MetricBadge server={s} sortField={sortField} />
                    </td>
                )}
                <td data-label="SSL Durumu">
                    <span style={{ backgroundColor: styles.bg, color: styles.color, padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        {d.label}
                    </span>
                </td>
            </tr>
            {expanded && (
                <tr className="expanded-row-details">
                    <td colSpan="5" style={{ padding: 0, backgroundColor: '#1a1a1a' }}>
                        <div style={{ padding: '20px 25px', borderBottom: '1px solid #333' }}>
                            <h5 style={{ margin: 0, color: '#ccc', fontSize: '13px', marginBottom: '15px', fontWeight: 'normal' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinelinejoin="round" style={{marginRight: '6px', verticalAlign: '-2px'}}>
                                    <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
                                    <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
                                    <line x1="6" y1="6" x2="6.01" y2="6"></line>
                                    <line x1="6" y1="18" x2="6.01" y2="18"></line>
                                </svg>
                                Bağlı Sunucu Detayları: <strong style={{color: '#fff'}}>{s.machineName}</strong>
                            </h5>
                            <div style={{ display: 'flex', gap: '30px', alignItems: 'center', backgroundColor: '#222', padding: '15px', borderRadius: '6px', flexWrap: 'wrap' }}>
                                <div className="row-flex-col">
                                    <span className="stat-text" style={{fontSize: '11px', color:'#aaa', marginBottom: '4px', display:'block'}}>CPU</span>
                                    <strong style={{fontSize: '15px', color: '#fff'}}>{s.cpu.toFixed(1)}%</strong>
                                </div>
                                <div className="row-flex-col">
                                    <span className="stat-text" style={{fontSize: '11px', color:'#aaa', marginBottom: '4px', display:'block'}}>RAM</span>
                                    <strong style={{fontSize: '13px', color: '#ccc'}}>{s.ramUsed.toFixed(1)}G / {s.ramTotal.toFixed(1)}G</strong>
                                    <ResourceBar used={s.ramUsed} total={s.ramTotal}/>
                                </div>
                                <div className="row-flex-col">
                                    <span className="stat-text" style={{fontSize: '11px', color:'#aaa', marginBottom: '4px', display:'block'}}>Disk</span>
                                    <strong style={{fontSize: '13px', color: '#ccc'}}>{s.diskUsed.toFixed(0)}G / {s.diskTotal.toFixed(0)}G</strong>
                                    <ResourceBar used={s.diskUsed} total={s.diskTotal}/>
                                </div>
                                <div className="row-flex-col">
                                    <span className="stat-text" style={{fontSize: '11px', color:'#aaa', marginBottom: '4px', display:'block'}}>API / DB Ping</span>
                                    <div style={{ display:'flex', gap:'5px', marginTop:'2px', flexDirection:'column' }}>
                                        {(() => {
                                            const apiSt = getStatusProps(s.apiResponseTime, 'API');
                                            const dbSt = getStatusProps(s.dbPerformance, 'DB');
                                            return (
                                                <>
                                                  <span style={{ backgroundColor: apiSt.bg, color: apiSt.color, padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>API: {s.apiResponseTime == null || s.apiResponseTime === -1 ? '-' : `${s.apiResponseTime}ms`}</span>
                                                  <span style={{ backgroundColor: dbSt.bg, color: dbSt.color, padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>DB: {s.dbPerformance == null || s.dbPerformance === -1 ? '-' : `${s.dbPerformance}ms`}</span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                                <div className="status-group" style={{marginLeft: 'auto'}}>
                                    {['Upload', 'Jpeg', 'Mail'].map((label) => {
                                        const field = `persits${label}`;
                                        return (
                                            <span key={label} className={`status ${s[field] ? 'ok' : 'fail'}`} style={{ padding: '4px 10px', fontSize: '11px', margin: '0 4px' }}>
                                                {label}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </React.Fragment>
    );
};

const GroupedDomainRow = ({ baseDomain, items, idx, forceExpand, sortField }) => {
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        setExpanded(forceExpand);
    }, [forceExpand]);

    const hasError = items.some(x => x.isExpired);
    const okCount = items.filter(x => !x.isExpired).length;
    const errCount = items.length - okCount;
    const label = hasError ? `${errCount} Hata` : `${items.length} Aktif`;
    const styleBg = hasError ? '#F44336' : '#4CAF50';

    return (
        <React.Fragment>
            <tr className={`domain-group-row ${expanded ? 'expanded' : ''}`} style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                <td data-label="İşlem" style={{ display: 'flex', alignItems: 'center', paddingLeft: '10px' }}>
                    <button 
                        onClick={() => setExpanded(!expanded)}
                        style={{ background: '#444', border: '1px solid #666', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', width: '24px', height: '24px', borderRadius: '4px', marginRight: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Subdomainleri Aç/Kapat"
                    >
                        {expanded ? '−' : '+'}
                    </button>
                    <span style={{ color: '#777' }}>#{idx + 1}</span>
                </td>
                <td data-label="Ağ Grubu" style={{ textAlign: 'left', paddingLeft: '20px' }}>
                    <strong style={{ color: '#fff', fontSize: '15px' }}>{baseDomain}</strong>
                    <span style={{ marginLeft: '10px', fontSize: '12px', color: '#888' }}>({items.length} Alt Kayıt)</span>
                </td>
                <td data-label="Özet" style={{ color: '#aaa', fontSize: '12px', fontStyle: 'italic' }}>Grup Özeti</td>
                <td data-label="IP" style={{ color: '#aaa', fontSize: '12px' }}>-</td>
                {sortField && (
                    <td data-label="Metrik" style={{ color: '#aaa', fontSize: '12px' }}>-</td>
                )}
                <td data-label="Durum">
                    <span style={{ backgroundColor: styleBg, color: '#fff', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        {label}
                    </span>
                </td>
            </tr>
            {expanded && items.map((subD, subIdx) => (
                <DomainRow 
                    key={`${subD.domain}-${subIdx}`} 
                    d={subD} 
                    i={subIdx} 
                    forceExpand={false} 
                    isSubRow={true} 
                    sortField={sortField}
                />
            ))}
        </React.Fragment>
    );
};


const DomainTable = ({ activeServers, sslErrorsOnly, groupSubdomains, expandAllDomains, onToggleExpandAll, searchTerm, sortField, sortDesc }) => {
    const allDomains = useMemo(() => {
        let list = [];
        activeServers.forEach(s => {
            if (s.sslParsed) {
                s.sslParsed.forEach(ssl => {
                    list.push({ ...ssl, server: s });
                });
            }
        });
        
        if (sslErrorsOnly) {
            list = list.filter(d => d.isExpired);
        }

        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            list = list.filter(d => 
                (d.domain && d.domain.toLowerCase().includes(lowerTerm)) ||
                (d.server.machineName && d.server.machineName.toLowerCase().includes(lowerTerm)) ||
                (d.server.machineIp && d.server.machineIp.toLowerCase().includes(lowerTerm))
            );
        }
        
        return list;
    }, [activeServers, sslErrorsOnly, searchTerm]);

    const groupedDomains = useMemo(() => {
        if (!groupSubdomains) return null;
        const groups = {};
        allDomains.forEach(d => {
             const base = getBaseDomain(d.domain);
             if (!groups[base]) groups[base] = [];
             groups[base].push(d);
        });

        const sortedBases = Object.keys(groups).sort((a,b) => {
            const aError = groups[a].some(x => x.isExpired);
            const bError = groups[b].some(x => x.isExpired);
            if (aError && !bError) return -1;
            if (!aError && bError) return 1;
            return a.localeCompare(b);
        });

        return sortedBases.map(b => ({ baseDomain: b, items: groups[b] }));
    }, [allDomains, groupSubdomains]);

    const flatSorted = useMemo(() => {
        if (groupSubdomains) return [];
        return [...allDomains].sort((a,b) => {
            if (a.isExpired && !b.isExpired) return -1;
            if (!a.isExpired && b.isExpired) return 1;
            
            if (sortField) {
                const getVal = (s) => {
                    if (sortField === "cpu") return s.cpu || 0;
                    if (sortField === "ram") return s.ramTotal > 0 ? (s.ramUsed / s.ramTotal) * 100 : 0;
                    if (sortField === "disk") return s.diskTotal > 0 ? (s.diskUsed / s.diskTotal) * 100 : 0;
                    if (sortField === "api") return (s.apiResponseTime === -1 || s.apiResponseTime == null) ? Infinity : s.apiResponseTime;
                    if (sortField === "db") return (s.dbPerformance === -1 || s.dbPerformance == null) ? Infinity : s.dbPerformance;
                    return 0;
                };
                const valA = getVal(a.server);
                const valB = getVal(b.server);
                if (valA !== valB) return sortDesc ? valB - valA : valA - valB; // Descending or Ascending
            }

            if (a.daysLeft !== null && b.daysLeft !== null) return a.daysLeft - b.daysLeft;
            return a.domain.localeCompare(b.domain);
        });
    }, [allDomains, groupSubdomains, sortField, sortDesc]);

    return (
        <div className="table-responsive-wrapper">
            <table className="server-table">
                <thead>
                    <tr>
                        <th style={{width: '60px', paddingLeft: '10px'}}>
                            <button 
                                onClick={onToggleExpandAll}
                                style={{ background: 'transparent', border: '1px solid #555', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', width: '24px', height: '24px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                title="Tümünü Aç/Kapat"
                            >
                                {expandAllDomains ? '−' : '+'}
                            </button>
                        </th>
                        <th style={{textAlign: 'left', paddingLeft: '20px'}}>Domain</th>
                        <th>Sunucu {groupSubdomains && '(Grup)'}</th>
                        <th>IP</th>
                        {sortField && <th>Metrik</th>}
                        <th>SSL Durumu</th>
                    </tr>
                </thead>
                <tbody>
                {groupSubdomains && groupedDomains ? (
                    groupedDomains.map((group, idx) => (
                        <GroupedDomainRow key={group.baseDomain} baseDomain={group.baseDomain} items={group.items} idx={idx} forceExpand={expandAllDomains} sortField={sortField} />
                    ))
                ) : (
                    flatSorted.map((d, i) => (
                        <DomainRow key={`${d.domain}-${d.server.machineName}`} d={d} i={i} forceExpand={expandAllDomains} sortField={sortField} />
                    ))
                )}
                {allDomains.length === 0 && (
                    <tr><td colSpan="5" style={{padding: '20px', color: '#aaa'}}>Gösterilecek kayıt bulunamadı.</td></tr>
                )}
                </tbody>
            </table>
        </div>
    );
};


// ======================= DASHBOARD MAIN =======================

export default function ServerMonitorDashboard({ onLogout }) {
    const [servers, setServers] = useState({});
    const [viewMode, setViewMode] = useState('servers'); // 'servers' | 'domains'
    const [sortField, setSortField] = useState("disk");
    const [sortDesc, setSortDesc] = useState(true);
    const [hideUnselected, setHideUnselected] = useState(true);
    const [persitsOnly, setPersitsOnly] = useState(true);
    const [domainErrorsOnly, setDomainErrorsOnly] = useState(false);
    const [groupSubdomains, setGroupSubdomains] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentTime, setCurrentTime] = useState(Date.now());
    
    const [expandAllServers, setExpandAllServers] = useState(false);
    const [expandAllDomains, setExpandAllDomains] = useState(false);

    const cpuHist = useRef({});

    useEffect(() => {
        const interval = setInterval(() => setCurrentTime(Date.now()), 10000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const conn = new HubConnectionBuilder()
            .withUrl(HUB_URL, {
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets
            })
            .configureLogging(LogLevel.Warning)
            .withAutomaticReconnect() // Kopsa bile otomatik bağlanır
            .build();

        // 1. İLK BAĞLANTI VE ODAYA GİRİŞ
        conn.start()
            .then(() => {
                console.log("✅ Hub'a Bağlanıldı");
                conn.invoke("SubscribeToAll")
                    .then(() => console.log("🔑 Admin yetkisiyle tüm verilere abone olundu."))
                    .catch(err => console.error("Oda abonelik hatası:", err));
            })
            .catch(err => console.error("❌ Hub Bağlantı Hatası:", err));

        // 2. YENİ EKLENEN: KOPUP TEKRAR BAĞLANIRSA ODAYA TEKRAR GİR
        conn.onreconnected((connectionId) => {
            console.log(`🔄 Bağlantı yenilendi (ID: ${connectionId}). Admin odasına tekrar giriliyor...`);
            conn.invoke("SubscribeToAll")
                .catch(err => console.error("Yeniden abonelik hatası:", err));
        });

        // 3. VERİLERİ DİNLEME (Sadece AdminRoom'a gönderilenler buraya düşecek)
        conn.on("ReceiveStatus", (p) => {
            const id = p.machineName;
            const t = new Date().toLocaleTimeString([], { hour12: false });
            
            const currentCpu = p.cpu || 0;
            cpuHist.current[id] = [...(cpuHist.current[id] || []), { value: currentCpu, time: t }].slice(-60);
            
            setServers((prev) => {
                const isReconnect = !!prev[id] && (Date.now() - (prev[id].lastUpdated || 0)) > 20000;
                return {
                    ...prev,
                    [id]: {
                        ...p,
                        cpuHistory: cpuHist.current[id],
                        lastUpdated: Date.now(),
                        justConnectedAt: !prev[id] || isReconnect ? Date.now() : prev[id].justConnectedAt
                    }
                };
            });
        });

        return () => conn.stop();
    }, []);

   const isStale = (s) => !s.lastUpdated || (currentTime - s.lastUpdated > 120000); 
const isUnresponsive = (s) => !s.lastUpdated || (currentTime - s.lastUpdated > 70000); 
    const isJustConnected = (s) => s.justConnectedAt && (currentTime - s.justConnectedAt < 10000);

    const processedServers = useMemo(() => {
        return Object.values(servers).map(s => {
            let sslParsed = [];
            let sslSummary = { status: 'ok', label: 'Yok', hasError: false };

            if (s.sslList && Array.isArray(s.sslList) && s.sslList.length > 0) {
                sslParsed = s.sslList.map(parseSslItem);
                const errors = sslParsed.filter(x => x.isExpired);
                const activeOnes = sslParsed.filter(x => !x.isExpired && x.daysLeft !== null);
                
                if (errors.length > 0) {
                    sslSummary = { status: 'fail', label: `${errors.length} Hata`, hasError: true };
                } else if (activeOnes.length > 0) {
                    const minDays = Math.min(...activeOnes.map(x => x.daysLeft));
                    sslSummary = { status: 'ok', label: `${minDays} Gün`, hasError: false };
                } else {
                    sslSummary = { status: 'ok', label: `OK`, hasError: false };
                }
            } else {
                sslSummary = { status: 'ok', label: `Veri Yok`, hasError: false };
            }

            return { ...s, sslParsed, sslSummary };
        });
    }, [servers]);

    const activeServers = useMemo(() => {
        return processedServers.filter(s => !isStale(s));
    }, [processedServers]);

    const sortedServers = useMemo(() => {
        const isPersitsFaulty = (s) => !s.persitsUpload || !s.persitsJpeg || !s.persitsMail;

        const getVal = (s) => {
            if (sortField === "cpu") return s.cpu || 0;
            if (sortField === "ram") return s.ramTotal > 0 ? (s.ramUsed / s.ramTotal) * 100 : 0;
            if (sortField === "disk") return s.diskTotal > 0 ? (s.diskUsed / s.diskTotal) * 100 : 0;
            if (sortField === "api") return (s.apiResponseTime === -1 || s.apiResponseTime == null) ? Infinity : s.apiResponseTime;
            if (sortField === "db") return (s.dbPerformance === -1 || s.dbPerformance == null) ? Infinity : s.dbPerformance;
            return 0;
        };

        let filtered = processedServers;
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(s => 
                (s.machineName && s.machineName.toLowerCase().includes(lowerTerm)) ||
                (s.machineIp && s.machineIp.toLowerCase().includes(lowerTerm)) ||
                (s.osVersion && s.osVersion.toLowerCase().includes(lowerTerm))
            );
        }

        return filtered.sort((a, b) => {
            if (persitsOnly) {
                const aFaulty = isPersitsFaulty(a);
                const bFaulty = isPersitsFaulty(b);
                if (aFaulty !== bFaulty) return bFaulty - aFaulty; 
            }
            const valA = getVal(a);
            const valB = getVal(b);
            return sortDesc ? valB - valA : valA - valB;
        });
    }, [processedServers, sortField, sortDesc, persitsOnly, searchTerm]);

    const totals = useMemo(() => {
        return {
            serverCount: activeServers.length,
            activeDomainTotal: activeServers.reduce((sum, s) => sum + (s.activeDomain || 0), 0),
            altDomainTotal: activeServers.reduce((sum, s) => sum + (s.altDomain || 0), 0),
            ramTotal: activeServers.reduce((sum, s) => sum + (s.ramTotal || 0), 0),
            ramUsed:  activeServers.reduce((sum, s) => sum + (s.ramUsed || 0), 0),
            diskTotal: activeServers.reduce((sum, s) => sum + (s.diskTotal || 0), 0),
            diskUsed:  activeServers.reduce((sum, s) => sum + (s.diskUsed || 0), 0)
        };
    }, [activeServers]);

    return (
        <div className="dashboard-container">
            <div className="main-content">
                <div className="table-section">
                    
                    {/* Üst Bar: Tablar ve Filtreler */}
                    <div className="top-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', backgroundColor: '#1a1a1a', padding: '10px 20px', borderRadius: '6px', flexWrap: 'wrap' }}>
                        <div className="view-toggle">
                            <button className={viewMode === 'servers' ? 'active' : ''} onClick={() => setViewMode('servers')}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinelinejoin="round" style={{marginRight: '6px', verticalAlign: '-2px'}}>
                                  <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
                                  <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
                                  <line x1="6" y1="6" x2="6.01" y2="6"></line>
                                  <line x1="6" y1="18" x2="6.01" y2="18"></line>
                                </svg>
                                Sunucular
                            </button>
                            <button className={viewMode === 'domains' ? 'active' : ''} onClick={() => setViewMode('domains')}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinelinejoin="round" style={{marginRight: '6px', verticalAlign: '-2px'}}>
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <line x1="2" y1="12" x2="22" y2="12"></line>
                                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                                </svg>
                                Domainler
                            </button>
                        </div>

                        {/* Search Bar Here */}
                        <div className="search-bar-wrapper" style={{ position: 'relative', margin: '10px 0', flex: '1', minWidth: '200px', maxWidth: '300px', marginInline: '20px' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '10px', top: '10px' }}>
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                            <input 
                                type="text" 
                                placeholder="Ara (Sunucu, IP, Domain)..." 
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{ width: '100%', padding: '8px 10px 8px 34px', backgroundColor: '#2a2a2a', border: '1px solid #444', color: '#fff', borderRadius: '4px', outline: 'none', fontSize: '13px' }}
                            />
                        </div>

                        {viewMode === 'servers' ? (
                            <div className="sort-options" style={{ margin: 0, display: 'flex', flexWrap: 'wrap' }}>
                                {[{key: 'cpu', label: 'CPU'}, {key: 'ram', label: 'RAM'}, {key: 'disk', label: 'DİSK'}, {key: 'api', label: 'API MS'}, {key: 'db', label: 'DB MS'}].map((item) => (
                                    <label key={item.key} className="custom-control-label">
                                        <input type="radio" value={item.key} checked={sortField === item.key} onChange={(e) => setSortField(e.target.value)} />
                                        <span className="custom-radio"></span>
                                        {item.label}
                                    </label>
                                ))}
                                <button 
                                    onClick={() => setSortDesc(!sortDesc)} 
                                    style={{ background: 'transparent', border: '1px solid #555', color: '#fff', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: '13px', marginLeft: '5px' }}>
                                    {sortDesc ? 'Z-A ↓' : 'A-Z ↑'}
                                </button>
                                <div className="vertical-divider mobile-only-toggle" style={{width: '1px', height: '18px', backgroundColor: '#333', margin: '0 5px'}}></div>
                                <label className="custom-control-label mobile-only-toggle" style={{ color: '#03a9f4', fontWeight: 'bold' }}>
                                    <input type="checkbox" checked={hideUnselected} onChange={(e) => setHideUnselected(e.target.checked)} />
                                    <span className="custom-checkbox"></span>
                                    Sadece Seçili Metriği Göster
                                </label>
                                <div className="vertical-divider" style={{width: '1px', height: '18px', backgroundColor: '#333', margin: '0 5px'}}></div>
                                <label className="custom-control-label">
                                    <input type="checkbox" checked={persitsOnly} onChange={(e) => setPersitsOnly(e.target.checked)} />
                                    <span className="custom-checkbox"></span>
                                    Persits Hatalıları Üste Al
                                </label>
                            </div>
                        ) : (
                            <div className="sort-options" style={{ margin: 0, display: 'flex', flexWrap: 'wrap' }}>
                                {[{key: 'cpu', label: 'CPU'}, {key: 'ram', label: 'RAM'}, {key: 'disk', label: 'DİSK'}, {key: 'api', label: 'API MS'}, {key: 'db', label: 'DB MS'}].map((item) => (
                                    <label key={item.key} className="custom-control-label">
                                        <input type="radio" value={item.key} checked={sortField === item.key} onChange={(e) => setSortField(e.target.value)} />
                                        <span className="custom-radio"></span>
                                        {item.label}
                                    </label>
                                ))}
                                <button 
                                    onClick={() => setSortDesc(!sortDesc)} 
                                    style={{ background: 'transparent', border: '1px solid #555', color: '#fff', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: '13px', marginLeft: '5px' }}>
                                    {sortDesc ? 'Z-A ↓' : 'A-Z ↑'}
                                </button>
                                <div className="vertical-divider mobile-only-toggle" style={{width: '1px', height: '18px', backgroundColor: '#333', margin: '0 5px'}}></div>
                                <label className="custom-control-label mobile-only-toggle" style={{ color: '#03a9f4', fontWeight: 'bold' }}>
                                    <input type="checkbox" checked={hideUnselected} onChange={(e) => setHideUnselected(e.target.checked)} />
                                    <span className="custom-checkbox"></span>
                                    Sadece Seçili Metriği Göster
                                </label>
                                <div className="vertical-divider" style={{width: '1px', height: '18px', backgroundColor: '#333', margin: '0 5px'}}></div>
                                <label className="custom-control-label">
                                    <input type="checkbox" checked={domainErrorsOnly} onChange={(e) => setDomainErrorsOnly(e.target.checked)} />
                                    <span className="custom-checkbox"></span>
                                    Sadece SSL Hatalılarını Göster
                                </label>
                                <div className="vertical-divider" style={{width: '1px', height: '18px', backgroundColor: '#333', margin: '0 5px'}}></div>
                                <label className="custom-control-label">
                                    <input type="checkbox" checked={groupSubdomains} onChange={(e) => setGroupSubdomains(e.target.checked)} />
                                    <span className="custom-checkbox"></span>
                                    Subdomainleri Grupla
                                </label>
                            </div>
                        )}

                        {onLogout && (
                            <button 
                                onClick={onLogout}
                                style={{
                                    marginLeft: 'auto',
                                    backgroundColor: 'transparent',
                                    border: '1px solid #ff3333',
                                    color: '#ff3333',
                                    padding: '6px 15px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: '13px',
                                    transition: 'all 0.3s'
                                }}
                                onMouseEnter={e => { e.target.style.backgroundColor = 'rgba(255, 51, 51, 0.1)'; e.target.style.textShadow = '0 0 8px #ff3333'; }}
                                onMouseLeave={e => { e.target.style.backgroundColor = 'transparent'; e.target.style.textShadow = 'none'; }}
                            >
                                Güvenli Çıkış
                            </button>
                        )}
                    </div>

                    {/* Tablo Alanı */}
                    <div style={{ paddingBottom: '20px' }}>
                        {viewMode === 'servers' ? (
                            <div className={`table-responsive-wrapper ${hideUnselected && sortField !== 'all' ? `mobile-compact mobile-sort-${sortField}` : 'mobile-sort-all'}`}>
                                <table className="server-table">
                                    <thead>
                                        <tr>
                                            <th style={{width: '60px', paddingLeft: '10px'}}>
                                                <button 
                                                    onClick={() => setExpandAllServers(!expandAllServers)}
                                                    style={{ background: 'transparent', border: '1px solid #555', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', width: '24px', height: '24px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    title="Tümünü Aç/Kapat"
                                                >
                                                    {expandAllServers ? '−' : '+'}
                                                </button>
                                            </th>
                                            <th>IP</th>
                                            <th>Name</th>
                                            <th onClick={() => { if (sortField === 'cpu') setSortDesc(!sortDesc); else { setSortField('cpu'); setSortDesc(true); }}} style={{ cursor: 'pointer', userSelect: 'none' }}>CPU {sortField === 'cpu' ? (sortDesc ? '↓' : '↑') : ''}</th>
                                            <th onClick={() => { if (sortField === 'ram') setSortDesc(!sortDesc); else { setSortField('ram'); setSortDesc(true); }}} style={{ cursor: 'pointer', userSelect: 'none' }}>RAM {sortField === 'ram' ? (sortDesc ? '↓' : '↑') : ''}</th>
                                            <th onClick={() => { if (sortField === 'disk') setSortDesc(!sortDesc); else { setSortField('disk'); setSortDesc(true); }}} style={{ cursor: 'pointer', userSelect: 'none' }}>Disk {sortField === 'disk' ? (sortDesc ? '↓' : '↑') : ''}</th>
                                            <th>
                                                <span onClick={() => { if (sortField === 'api') setSortDesc(!sortDesc); else { setSortField('api'); setSortDesc(true); }}} style={{ cursor: 'pointer', userSelect: 'none' }}>API {sortField === 'api' ? (sortDesc ? '↓' : '↑') : ''}</span> / <span onClick={() => { if (sortField === 'db') setSortDesc(!sortDesc); else { setSortField('db'); setSortDesc(true); }}} style={{ cursor: 'pointer', userSelect: 'none' }}>DB {sortField === 'db' ? (sortDesc ? '↓' : '↑') : ''}</span> Ping
                                            </th>
                                            <th>Persits & SSL</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {sortedServers.map((s, i) => (
                                        <ServerRow key={s.machineName} s={s} i={i} isStale={isUnresponsive} isJustConnected={isJustConnected} forceExpand={expandAllServers} />
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className={`table-responsive-wrapper domain-table-mobile-sort ${hideUnselected && sortField !== 'all' ? `mobile-compact mobile-sort-${sortField}` : 'mobile-sort-all'}`}>
                                <DomainTable 
                                    activeServers={activeServers} 
                                    sslErrorsOnly={domainErrorsOnly} 
                                    groupSubdomains={groupSubdomains}
                                    expandAllDomains={expandAllDomains} 
                                    onToggleExpandAll={() => setExpandAllDomains(!expandAllDomains)} 
                                    searchTerm={searchTerm}
                                    sortField={sortField}
                                    sortDesc={sortDesc}
                                />
                            </div>
                        )}
                    </div>

                </div>

                <div className="domain-sidebar">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '10px 0 25px 0', borderBottom: '1px solid #333', marginBottom: '20px' }}>
                        <img src="/logo.svg" alt="BöcekSoft Logo" style={{ height: '40px', objectFit: 'contain' }} />
                        <span style={{ marginTop: '8px', fontSize: '11px', color: '#888', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: '300', textAlign: 'center' }}>
                            BöcekSoft Servers Monitoring
                        </span>
                    </div>
                    <SystemStats totals={totals} />
                </div>
            </div>
        </div>
    );
}